import { Component, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl } from '@angular/forms'
import { BasicFieldType } from '@app/v2/shared/enums/field.enum'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { environment } from '@environments/environment'
import { debounceTime, Subject } from 'rxjs'
import { SelectInputComponent } from '../select-input/select-input.component'

declare var google: any

type CityOption = {
  place_name: string
  place_id: string
  structured_formatting: any
}

@Component({
  selector: 'city-input',
  templateUrl: './city-input.component.html',
  styleUrls: ['./city-input.component.scss'],
  standalone: true,
  imports: [SelectInputComponent]
})
export class CityInputComponent implements OnInit, OnDestroy {
  // Form Control
  @Input() control!: FormControl
  // Label for input field
  @Input() label: string = 'City of residence'
  // placeholder for input field
  @Input() placeHolder: string = ''
  // Icon for input field
  @Input() icon!: string
  // ID for input field
  @Input() id: string = 'city-input'
  // Placeholder for input field
  @Input() placeholder: string = ''
  // Icon for input field
  @Input() iconType!: string
  // Flag for Clear button for input field
  @Input() clear: boolean = false
  // Flag to disable control manually
  @Input() isDisabled: boolean = false
  @Input() isDependentOnCountry: boolean = false
  // Flag to search for address / city / country
  @Input() search: '(cities)' | 'address' | 'country' = '(cities)'
  // Country code to filter cities
  @Input() countryCode: string | null = null
  @Input() required: boolean = false
  @Input() mode: BasicInputMode = BasicInputMode.DEFAULT
  @Input() fieldType: BasicFieldType = BasicFieldType.SELECT
  // Emit value for parent component
  @Output() valueChanged: EventEmitter<string> = new EventEmitter<string>()
  @Output() countryISOCodeChanged: EventEmitter<string | null> = new EventEmitter<string | null>()
  @Output() onKeydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>()
  @Output() onBlurValueChange: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>()

  @ViewChild(SelectInputComponent) private _selectInput?: SelectInputComponent

  public options: CityOption[] = []
  public hasSearchQuery: boolean = false
  private _searchSubject = new Subject<string>()
  private _autocompleteService: any
  private _sessionToken: any
  private _previousValue: string = ''
  /** Last confirmed city (select / parent prefill). Empty keyboard clear must not wipe this. */
  private _committedValue: string = ''
  /** Latest search term — used to drop stale debounce / Places callbacks. */
  private _latestSearchTerm: string = ''
  private _destroyRef = inject(DestroyRef)

  ngOnInit(): void {
    this._initializeGoogleMaps()
    this._setupSearchSubscription()
    this._committedValue = this.control.value || ''
    this._previousValue = this._committedValue
    this._syncCommittedValueFromControl()
  }

  ngOnDestroy(): void {
    this._searchSubject.complete()
  }

  /**
   * Keep committed value in sync when parent prefills/patches the control
   */
  private _syncCommittedValueFromControl(): void {
    this.control.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      const next = value || ''
      if (next && next !== this._committedValue && !this._selectInput?.isFocused) {
        this._committedValue = next
        this._previousValue = next
      }
    })
  }

  /**
   * @description Initializes Google Maps
   * @returns void
   */
  private _initializeGoogleMaps(): void {
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      this._autocompleteService = new google.maps.places.AutocompleteService()
      this._sessionToken = new google.maps.places.AutocompleteSessionToken()
    } else this._loadGoogleMapsAPI()
  }

  /**
   * @description Loads Google Maps API
   * @returns void
   */
  private _loadGoogleMapsAPI(): void {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsAPIKey}&libraries=places`
    script.async = true
    script.defer = true
    script.onload = () => {
      this._autocompleteService = new google.maps.places.AutocompleteService()
      this._sessionToken = new google.maps.places.AutocompleteSessionToken()
    }
    document.head.appendChild(script)
  }

  /**
   * @description Sets up search subscription
   * @returns void
   */
  private _setupSearchSubscription(): void {
    this._searchSubject.pipe(debounceTime(500), takeUntilDestroyed(this._destroyRef)).subscribe((searchTerm) => {
      // Drop debounced terms that are no longer current (e.g. user cleared input)
      if (searchTerm !== this._latestSearchTerm || !searchTerm) return
      this._searchCities(searchTerm)
    })
  }

  /**
   * @description Performs search
   * @param {string} searchTerm
   * @returns void
   */
  public onSearch(searchTerm: string): void {
    this._latestSearchTerm = searchTerm || ''
    this.hasSearchQuery = !!searchTerm

    // rewrites selectedValue from control (last-letter snap-back). Keep last options idle.
    if (!searchTerm) {
      this._searchSubject.next('')
      return
    }
    this._searchSubject.next(searchTerm)
  }

  /**
   * @description Searches for cities using Google Places API
   * @param {string} searchTerm
   * @returns void
   */
  private _searchCities(searchTerm: string): void {
    if (!this._autocompleteService) return

    const request: any = {
      input: searchTerm,
      types: ['(regions)'], // This might give better city, country format
      sessionToken: this._sessionToken
    }

    // Add country restriction if specified
    if (this.countryCode) {
      request.componentRestrictions = {
        country: this.countryCode
      }
    }

    this._autocompleteService.getPlacePredictions(request, (predictions: google.maps.places.PlaceResult[], status: google.maps.places.PlacesServiceStatus) => {
      if (searchTerm !== this._latestSearchTerm) return

      if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
        this.options = predictions.map((prediction: any) => ({
          place_name: this._formatCityCountry(prediction.description),
          place_id: prediction.place_id,
          structured_formatting: prediction.structured_formatting
        }))
      } else if (this._latestSearchTerm) this.options = []
    })
  }

  /**
   * @description Formats description to city, country format
   * @param description Full description from API
   * @returns Formatted city, country string
   */
  private _formatCityCountry(description: string): string {
    const parts = description.split(', ')
    if (parts.length >= 2) {
      // Take first part (city) and last part (country)
      return `${parts[0]}, ${parts[parts.length - 1]}`
    }
    return description
  }

  /**
   * @description Extract country ISO code from place name
   * @param data City options
   * @param placeName Place name
   * @returns country code
   */
  private _extractCountryISOCodeFromPlaceName(data: any[], placeName: string): string | null {
    const place = data?.find((item: any) => item.place_name.includes(placeName))
    if (place && place.context) return this._getCountryISOCodeFromContext(place.context)
    return null
  }

  /**
   * @description Get country ISO code from context
   * @param context Context
   * @returns country code
   * */
  private _getCountryISOCodeFromContext(context: any[]): string | null {
    const countryContext = context.find((item: any) => item.id.startsWith('country'))
    return countryContext ? countryContext.short_code.toUpperCase() : null
  }

  /**
   * @description Emits value changed event
   * @param {string} value
   * @returns void
   */
  public onValueChange(value: string | null) {
    const stringValue = value || ''

    if (!stringValue) return

    // Only proceed if the value has actually changed
    if (stringValue === this._previousValue) return

    this._previousValue = stringValue
    this._committedValue = stringValue
    this.control.setValue(stringValue, { emitEvent: false })
    this.control.markAsDirty()
    const countryISOCode = this._extractCountryISOCodeFromPlaceName(this.options, stringValue)
    this.valueChanged.emit(stringValue)
    this.countryISOCodeChanged.emit(countryISOCode)
  }

  /**
   * Forward keydown event to parent
   * @param {KeyboardEvent} event
   */
  public onKeydownEvent(event: KeyboardEvent): void {
    this.onKeydown.emit(event)
  }

  /**
   * Forward blur event to parent. Runs after select-input dropdownClosed setTimeout
   * @param {FocusEvent} event
   */
  public onBlurEvent(event: FocusEvent): void {
    setTimeout(() => {
      this._restoreCommittedDisplay()
      this.onBlurValueChange.emit(event)
    })
  }

  /**
   * Put last committed city back into the control + select display after empty edit/blur.
   */
  private _restoreCommittedDisplay(): void {
    if (!this._committedValue && this.control.value) {
      this._committedValue = this.control.value
    }
    if (!this._committedValue) {
      this._previousValue = ''
      return
    }

    this.control.setValue(this._committedValue)
    this._previousValue = this._committedValue

    if (this._selectInput) {
      this._selectInput.selectedValue = this._committedValue
      this._selectInput.hasSearchQuery = false
      const el = this._selectInput.selectInput?.nativeElement
      if (el) el.value = this._committedValue
    }
  }
}
